"use client";

import styled from "styled-components";
import { formatMoney, formatPercent } from "@/utils/format";
import { SummaryCard } from "./summary-card";
import { EstimateBadge } from "@/components/ui/estimate-indicator";
import { Banknote, Wallet, TrendingUp, Percent } from "lucide-react";
import type { AggregatedSummary } from "./types";

interface SummaryCardsProps {
  summary?: AggregatedSummary;
  isLoading: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EstimateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EstimateText = styled.span`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export function SummaryCards({ summary, isLoading }: SummaryCardsProps) {
  // Данные из агрегированного ответа бэкенда
  const revenue = summary?.revenue ?? "0";
  const toTransfer = summary?.to_transfer ?? "0";
  const profitBeforeTax = summary?.profit_before_tax ?? "0";
  const roi = summary?.roi ?? "0";
  const ordersCount = summary?.orders_count ?? 0;
  const salesCount = summary?.sales_count ?? 0;
  const hasEstimates = summary?.has_estimates ?? false;
  const estimateDates = summary?.estimate_dates ?? [];

  const hasData = !!summary;
  const profitValue = parseFloat(profitBeforeTax);
  const profitVariant = profitValue >= 0 ? "success" : "danger";

  return (
    <Container>
      {hasData && hasEstimates && (
        <EstimateRow>
          <EstimateBadge estimateDates={estimateDates} totalDays={estimateDates.length} />
          <EstimateText>Часть данных предварительная</EstimateText>
        </EstimateRow>
      )}
      <CardsGrid>
        <SummaryCard
          title="Выручка"
          value={hasData ? formatMoney(revenue) : "\u2014"}
          icon={Banknote}
          subtitle={hasData ? `${ordersCount} заказов` : undefined}
          isLoading={isLoading}
          hasEstimate={hasEstimates}
        />
        <SummaryCard
          title="К перечислению"
          value={hasData ? formatMoney(toTransfer) : "\u2014"}
          icon={Wallet}
          subtitle={hasData ? `${salesCount} продаж` : undefined}
          isLoading={isLoading}
          hasEstimate={hasEstimates}
        />
        <SummaryCard
          title="Прибыль до налогов"
          value={hasData ? formatMoney(profitBeforeTax) : "\u2014"}
          icon={TrendingUp}
          variant={profitVariant}
          subtitle="после всех расходов"
          isLoading={isLoading}
          hasEstimate={hasEstimates}
        />
        <SummaryCard
          title="ROI"
          value={hasData ? formatPercent(roi) : "\u2014"}
          icon={Percent}
          variant={profitVariant}
          subtitle="прибыль / себестоимость"
          isLoading={isLoading}
          hasEstimate={hasEstimates}
        />
      </CardsGrid>
    </Container>
  );
}
